import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  CircularProgress,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Paper,
  Divider,
} from '@mui/material';
import { useService } from '../../../contexts/ServiceContext';
import { Branding, useBrand } from '../../../contexts/BrandingContext';
import { useAuth } from '../../../contexts/AuthContext';
import { Logo } from '../../../system/branding';
import ThemeSelector from './ThemeSelector';
import ThemeOptions from './ThemeOptions';
import LogoSettings from './LogoSettings';
import CustomContent from './CustomContent';
import UnauthenticatedContent from './UnauthenticatedContent';
import PrimaryColorsPanel from './PrimaryColorsPanel';
import { themePresets } from './ThemePresets';
import { defaultBrandColors } from '../../../contexts/ThemeContext';

// TabPanel component to handle tab content
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`branding-tabpanel-${index}`}
      aria-labelledby={`branding-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `branding-tab-${index}`,
    'aria-controls': `branding-tabpanel-${index}`,
  };
};

const AdminBranding: React.FC = () => {
  const { post } = useService();
  const { branding: currentBranding, loadBranding } = useBrand();
  const { session } = useAuth();

  // State management
  const [branding, setBranding] = useState<Branding>({
    name: '',
    version: '',
    themeoptions: {
      palette: {
        primary: { main: '#F13C00' },
        secondary: { main: '#1f3473' },
      },
    },
    logo: {
      url: '',
      altText: '',
      width: 0,
      height: 0,
    },
    selectedTheme: 'default',
    usecustomheaderandfooterunauthenticated: false,
    customunauthenticatedheader: '',
    customunauthenticatedfooter: '',
  });

  const [tabValue, setTabValue] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isDirty, setIsDirty] = useState(false);

  // Load current branding data
  useEffect(() => {
    if (currentBranding) {
      setBranding({
        ...currentBranding,
        selectedTheme: currentBranding.selectedTheme || 'default',
      });
    }
  }, [currentBranding]);

  // Handle tab changes
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Handle input field changes
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setBranding((prev) => ({ ...prev, [name]: value }));
    setIsDirty(true);
  };

  // Handle logo field changes
  const handleLogoInputChange = (name: string, value: string | number) => {
    setBranding((prev) => ({
      ...prev,
      logo: {
        ...prev.logo,
        [name]: value,
      } as Logo,
    }));
    setIsDirty(true);
  };

  // Handle switch toggle
  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setBranding((prev) => ({ ...prev, [name]: checked }));
    setIsDirty(true);
  };

  // Handle unauthenticated content switch
  const handleUnauthenticatedSwitchChange = (checked: boolean) => {
    setBranding((prev) => ({
      ...prev,
      usecustomheaderandfooterunauthenticated: checked,
    }));
    setIsDirty(true);
  };

  // Helper function to update a nested value in an object using a path array
  const updateNestedValue = (obj: any, path: string[], value: any): any => {
    if (path.length === 0) return value;

    const current = { ...obj };
    let temp = current;

    for (let i = 0; i < path.length - 1; i++) {
      if (!temp[path[i]]) temp[path[i]] = {};
      temp[path[i]] = { ...temp[path[i]] };
      temp = temp[path[i]];
    }

    temp[path[path.length - 1]] = value;
    return current;
  };

  // Handle color change for nested properties
  const handleColorChange = (path: string[], color: string) => {
    setBranding((prev) => {
      const updatedThemeOptions = updateNestedValue(
        prev.themeoptions || {},
        path,
        color,
      );

      return {
        ...prev,
        themeoptions: updatedThemeOptions,
      };
    });
    setIsDirty(true);
  };

  // Handle theme options update
  const handleThemeOptionsUpdate = (newThemeOptions: any) => {
    setBranding((prev) => ({
      ...prev,
      themeoptions: newThemeOptions,
    }));
    setIsDirty(true);
  };

  // Handle theme selection
  const handleThemeSelect = (themeId: string) => {
    const selectedPreset = themePresets.find((preset) => preset.id === themeId);

    if (!selectedPreset) return;

    // Apply theme immediately without saving to database
    let updatedBranding = {
      ...branding,
      selectedTheme: themeId,
    };

    // If it's the default theme, reset all colors to defaults from ThemeContext
    if (themeId === 'default') {
      updatedBranding = {
        ...updatedBranding,
        themeoptions: {
          ...updatedBranding.themeoptions,
          palette: {
            primary: {
              main: defaultBrandColors.primary.main,
              light: defaultBrandColors.primary.light,
              dark: defaultBrandColors.primary.dark,
              contrastText: defaultBrandColors.primary.contrastText,
            },
            secondary: {
              main: defaultBrandColors.secondary.main,
              light: defaultBrandColors.secondary.light,
              medium: defaultBrandColors.secondary.medium,
              dark: defaultBrandColors.secondary.dark,
              contrastText: defaultBrandColors.secondary.contrastText,
            },
            error: {
              main: defaultBrandColors.error.main,
              light: defaultBrandColors.error.light,
              medium: defaultBrandColors.error.medium,
              dark: defaultBrandColors.error.dark,
            },
            warning: {
              main: defaultBrandColors.warning.main,
              light: defaultBrandColors.warning.light,
              dark: defaultBrandColors.warning.dark,
            },
            success: {
              main: defaultBrandColors.success.main,
              light: defaultBrandColors.success.light,
              dark: defaultBrandColors.success.dark,
            },
            grey: { ...(defaultBrandColors.grey as any) },
            states: { ...defaultBrandColors.states },
            text: {
              primary: defaultBrandColors.grey[700],
              secondary: defaultBrandColors.grey[500],
              disabled: defaultBrandColors.grey[500],
            },
            background: {
              default: '#ffffff',
              paper: '#ffffff',
              secondary: '#e8edf6',
            },
          },
        },
      };
    }
    // If it's a preset but not custom, apply the preset colors
    else if (themeId !== 'custom') {
      updatedBranding = {
        ...updatedBranding,
        themeoptions: {
          ...updatedBranding.themeoptions,
          palette: {
            ...updatedBranding.themeoptions?.palette,
            primary: {
              ...updatedBranding.themeoptions?.palette?.primary,
              main: selectedPreset.colors.primary,
            },
            secondary: {
              ...updatedBranding.themeoptions?.palette?.secondary,
              main: selectedPreset.colors.secondary,
            },
          },
        },
      };
    }
    // For custom theme, keep existing colors

    setBranding(updatedBranding);
    setIsDirty(true);
  };
  // Handle form submission
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      await post('save-branding', {}, branding);
      setSuccessMessage('Branding updated successfully');
      if (session) {
        await loadBranding(session);
      }
      setIsDirty(false);
    } catch (error) {
      setErrorMessage('Failed to update branding');
      console.error('Error saving branding:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Branding Configuration
        </Typography>

        <Typography variant="body1" sx={{ mb: 4 }}>
          Customize your application&apos;s look and feel by adjusting the
          branding settings below. All changes are previewed immediately but
          only saved when you click &quot;Save Changes&quot;.
        </Typography>

        <Paper sx={{ width: '100%', mb: 4 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="branding tabs"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Theme" {...a11yProps(0)} />
            <Tab label="Advanced Theming" {...a11yProps(1)} />
            <Tab label="Logo" {...a11yProps(2)} />
            <Tab label="Headers & Footers" {...a11yProps(3)} />
            <Tab label="Custom CSS" {...a11yProps(4)} />
            <Tab label="System Info" {...a11yProps(5)} />
          </Tabs>

          <form onSubmit={handleSubmit}>
            {/* Theme Selection Tab */}
            <TabPanel value={tabValue} index={0}>
              <ThemeSelector
                presets={themePresets}
                selectedTheme={branding.selectedTheme || 'default'}
                onSelectTheme={handleThemeSelect}
              />

              <Divider sx={{ my: 3 }} />

              {/* New Primary Colors Panel component */}
              <PrimaryColorsPanel
                themeOptions={branding.themeoptions}
                onColorChange={handleColorChange}
              />

              <Divider sx={{ my: 3 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={branding.isdarktheme || false}
                      onChange={handleSwitchChange}
                      name="isdarktheme"
                    />
                  }
                  label="Use Dark Theme"
                />

                <Typography variant="body2" color="text.secondary">
                  For best results with dark theme, customize the Background and
                  Text Colors above.
                </Typography>
              </Box>
            </TabPanel>

            {/* Advanced Theme Options Tab */}
            <TabPanel value={tabValue} index={1}>
              <ThemeOptions
                themeOptions={branding.themeoptions}
                onUpdateThemeOptions={handleThemeOptionsUpdate}
              />
            </TabPanel>

            {/* Logo Tab */}
            <TabPanel value={tabValue} index={2}>
              <LogoSettings
                logo={branding.logo}
                onChange={handleLogoInputChange}
              />
            </TabPanel>

            {/* Headers & Footers Tab */}
            <TabPanel value={tabValue} index={3}>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="subtitle2">
                  Important notes about headers:
                </Typography>
                <ul>
                  <li>
                    A custom header completely replaces the Bright MLS mega menu
                  </li>
                  <li>
                    If you want to keep the mega menu structure but use a custom
                    logo, don&apos;t provide a custom header - just upload a
                    logo in the Logo Settings tab
                  </li>
                </ul>
              </Alert>

              <CustomContent
                type="header"
                value={branding.customheader || ''}
                onChange={handleInputChange}
              />

              <CustomContent
                type="footer"
                value={branding.customfooter || ''}
                onChange={handleInputChange}
              />

              <Divider sx={{ my: 4 }} />

              <UnauthenticatedContent
                useCustom={
                  branding.usecustomheaderandfooterunauthenticated || false
                }
                header={branding.customunauthenticatedheader || ''}
                footer={branding.customunauthenticatedfooter || ''}
                onToggleUseCustom={handleUnauthenticatedSwitchChange}
                onHeaderChange={handleInputChange}
                onFooterChange={handleInputChange}
              />
            </TabPanel>

            {/* Custom CSS Tab */}
            <TabPanel value={tabValue} index={4}>
              <CustomContent
                type="css"
                value={branding.customcss || ''}
                onChange={handleInputChange}
              />
            </TabPanel>

            {/* System Info Tab */}
            <TabPanel value={tabValue} index={5}>
              <TextField
                fullWidth
                margin="normal"
                name="name"
                label="Brand Name"
                value={branding.name}
                InputProps={{ readOnly: true }}
                helperText="The name of your brand or application (read-only)"
              />
              <TextField
                fullWidth
                margin="normal"
                name="version"
                label="Version"
                value={branding.version}
                InputProps={{ readOnly: true }}
                helperText="Current version of the branding configuration (read-only)"
              />
            </TabPanel>

            <Box
              sx={{
                p: 3,
                borderTop: 1,
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              {isDirty && (
                <Typography variant="body2" color="error">
                  You have unsaved changes
                </Typography>
              )}
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isLoading || !isDirty}
                size="large"
              >
                {isLoading ? <CircularProgress size={24} /> : 'Save Changes'}
              </Button>
            </Box>
          </form>
        </Paper>
      </Box>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage('')}
      >
        <Alert onClose={() => setSuccessMessage('')} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage('')}
      >
        <Alert onClose={() => setErrorMessage('')} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminBranding;
